import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

type LoopPhase = 'forward' | 'slowing-down' | 'reversing' | 'speeding-up';

const HeroSection = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [loopPhase, setLoopPhase] = useState<LoopPhase>('forward');
  
  // Animation control refs
  const animationRef = useRef<number | null>(null);
  const reverseStartTime = useRef<number>(0);
  const reverseFromPosition = useRef<number>(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleLoadedData = () => {
      setIsVideoLoaded(true);
      video.playbackRate = 1;
      setLoopPhase('forward');
    };

    // Seamless reverse-play loop implementation
    const handleTimeUpdate = () => {
      const duration = video.duration;
      const currentTime = video.currentTime;
      
      // When video reaches near the end, start the seamless reverse sequence
      if (loopPhase === 'forward' && duration - currentTime <= 0.5) {
        startReverseSequence();
      }
    };

    const startReverseSequence = () => {
      const video = videoRef.current;
      if (!video) return;

      setLoopPhase('slowing-down');
      const startTime = performance.now();
      reverseStartTime.current = startTime;
      reverseFromPosition.current = video.currentTime;
      
      // Phase 1: Gradually slow down to a stop
      const slowDownPhase = () => {
        const elapsed = performance.now() - startTime;
        const slowDownDuration = 800; // 0.8 seconds to slow down
        
        if (elapsed < slowDownDuration) {
          // Gradually reduce playback rate from 1 to 0
          const progress = elapsed / slowDownDuration;
          const easeOut = 1 - Math.pow(1 - progress, 3); // Cubic ease-out
          video.playbackRate = 1 - easeOut;
          
          animationRef.current = requestAnimationFrame(slowDownPhase);
        } else {
          // Phase 2: Start reverse playback
          video.playbackRate = 0;
          setLoopPhase('reversing');
          startReversePlayback();
        }
      };
      
      animationRef.current = requestAnimationFrame(slowDownPhase);
    };

    const startReversePlayback = () => {
      const video = videoRef.current;
      if (!video) return;

      const startTime = performance.now();
      const reverseDuration = 4000; // 4 seconds of reverse playback
      const reverseDistance = 4; // Go back 4 seconds in the video
      const startPosition = reverseFromPosition.current;
      
      const reversePhase = () => {
        const elapsed = performance.now() - startTime;
        
        if (elapsed < reverseDuration) {
          // Smoothly interpolate backwards through the video
          const progress = elapsed / reverseDuration;
          const easeInOut = progress < 0.5 
            ? 2 * progress * progress 
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;
          
          const currentPosition = startPosition - (reverseDistance * easeInOut);
          video.currentTime = Math.max(0, currentPosition);
          
          animationRef.current = requestAnimationFrame(reversePhase);
        } else {
          // Phase 3: Gradually speed up forward playback
          setLoopPhase('speeding-up');
          startSpeedUpPhase();
        }
      };
      
      animationRef.current = requestAnimationFrame(reversePhase);
    };

    const startSpeedUpPhase = () => {
      const video = videoRef.current;
      if (!video) return;

      const startTime = performance.now();
      const speedUpDuration = 1000; // 1 second to reach normal speed
      
      const speedUpPhase = () => {
        const elapsed = performance.now() - startTime;
        
        if (elapsed < speedUpDuration) {
          // Gradually increase playback rate from 0 to 1
          const progress = elapsed / speedUpDuration;
          const easeIn = progress * progress * progress; // Cubic ease-in
          video.playbackRate = easeIn;
          
          animationRef.current = requestAnimationFrame(speedUpPhase);
        } else {
          // Return to normal forward playback
          video.playbackRate = 1;
          setLoopPhase('forward');
        }
      };
      
      animationRef.current = requestAnimationFrame(speedUpPhase);
    };

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      
      // Clean up any running animations
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [loopPhase]);

  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const textVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Background Video */}
      <div className="absolute inset-0 w-full h-full">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.7)' }}
        >
          <source src="/media/welcome_video_1.mp4" type="video/mp4" />
          {/* Fallback for browsers that don't support video */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary-bg via-primary-secondary to-primary-bg" />
        </video>
        
        {/* Video Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Hero Content */}
      <div className="relative z-10 h-full flex items-center justify-center">
        <div className="text-center space-y-8 px-6">
          
          {/* Animated Text Sequence */}
          <div className="space-y-6">
            {/* First Text: "Power analytics" */}
            <motion.h1
              className="text-6xl md:text-8xl font-display font-bold text-white text-shadow"
              variants={textVariants}
              initial="hidden"
              animate="visible"
              transition={{ duration: 1, delay: 1 }}
            >
              Power analytics
            </motion.h1>

            {/* Second Text: "Always one step ahead" */}
            <motion.h2
              className="text-2xl md:text-4xl font-medium text-white/90 text-shadow"
              variants={textVariants}
              initial="hidden"
              animate="visible"
              transition={{ duration: 1, delay: 2.5 }}
            >
              Always one step ahead
            </motion.h2>

            {/* Third Text: "Made accessible" */}
            <motion.h3
              className="text-xl md:text-2xl font-light text-white/80 text-shadow"
              variants={textVariants}
              initial="hidden"
              animate="visible"
              transition={{ duration: 1, delay: 4 }}
            >
              Made accessible
            </motion.h3>
          </div>

        </div>
      </div>

      {/* Scroll Indicator - Fixed positioning for perfect centering */}
      <motion.div
        className="absolute bottom-8 left-0 right-0 flex justify-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 5 }}
      >
        <button
          onClick={scrollToServices}
          className="flex flex-col items-center space-y-2 text-white/80 hover:text-white transition-colors group"
        >
          <span className="text-sm font-medium text-shadow">Scroll down for more</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <ChevronDown size={24} className="group-hover:text-primary-accent transition-colors" />
          </motion.div>
        </button>
      </motion.div>

      {/* Loading State */}
      {!isVideoLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-primary-bg via-primary-secondary to-primary-bg flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-white/60">Loading...</p>
          </div>
        </div>
      )}
    </section>
  );
};

export default HeroSection;

